import React from "react";
import { Modal } from "@mui/material";
import { XIcon, DocumentTextIcon } from "@heroicons/react/solid";
import { Button } from "../../components/ui";
import { getFileType, getFileName } from "../../utility";
import InlineLoader from "../../components/loader/InlineLoader";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

const PettyCashDetailModal = ({ open, onClose, transaction, isLoading }) => {
  return (
    <Modal open={open} onClose={onClose}>
      <div className="w-full h-full md:h-auto md:w-[90%] lg:w-[80%] xl:w-[70%] max-w-4xl bg-white rounded-none md:rounded-2xl shadow-2xl absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 overflow-hidden border-0 md:border md:border-gray-155 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-800">Transaction Details</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-6 flex-grow overflow-y-auto md:max-h-[80vh] flex flex-col">
          {isLoading ? (
            <div className="flex justify-center py-10 flex-1 items-center">
              <InlineLoader className="w-8 h-8 text-primary" />
            </div>
          ) : transaction ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6 flex-1">
              <div className="space-y-4">
                <div>
                  <span className="text-xs text-gray-400 font-semibold block uppercase">Date</span>
                  <span className="font-semibold text-gray-800">{transaction.expenses_date}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-400 font-semibold block uppercase">Amount</span>
                  <span className={`font-extrabold text-xl ${transaction.transaction_type === "CR" ? "text-green-600" : "text-red-600"
                    }`}>
                    {transaction.transaction_type === "CR" ? "+" : "-"}&#8377;{transaction.amount}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-gray-400 font-semibold block uppercase">Type</span>
                  <span className="font-semibold text-gray-800">
                    {transaction.transaction_type === "CR" ? "Credit / Income" : "Debit / Expense"}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-gray-400 font-semibold block uppercase">Category</span>
                  <span className="font-semibold text-gray-800">{transaction.expenses_type_name || "-"}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-400 font-semibold block uppercase">Particulars</span>
                  <span className="font-semibold text-gray-800 block">{transaction.particulars}</span>
                </div>
                <div>
                  <span className="text-xs text-gray-400 font-semibold block uppercase">Approval Status</span>
                  <span className="mt-1 inline-block">
                    <span className={
                      transaction.approval_status === "APPROVED" ? "status-approved" :
                        transaction.approval_status === "PENDING" ? "status-pending" : "status-rejected"
                    }>
                      {transaction.approval_status}
                    </span>
                  </span>
                </div>
                {transaction.invoice_no && (
                  <div>
                    <span className="text-xs text-gray-400 font-semibold block uppercase">Invoice/Voucher No</span>
                    <span className="font-semibold text-gray-800">{transaction.invoice_no}</span>
                  </div>
                )}

                {(transaction.chq_no || transaction.chq_date) && (
                  <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-4">
                    <div>
                      <span className="text-xs text-gray-400 font-semibold block uppercase">Cheque No</span>
                      <span className="font-semibold text-gray-800">{transaction.chq_no || "-"}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 font-semibold block uppercase">Cheque Date</span>
                      <span className="font-semibold text-gray-800">{transaction.chq_date || "-"}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4 flex flex-col justify-between h-full">
                <div className="space-y-4 flex-1">
                  {transaction.additional_remarks && (
                    <div>
                      <span className="text-xs text-gray-400 font-semibold block uppercase mb-1">Additional Remarks</span>
                      <span className="text-sm text-gray-700 block bg-gray-50 p-2.5 rounded-xl border border-gray-150">
                        {transaction.additional_remarks}
                      </span>
                    </div>
                  )}

                  {(transaction.remarks || transaction.rejection_remarks) && (
                    <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-700">
                      <span className="text-xs font-bold block uppercase mb-1">Rejection Reason</span>
                      <span className="text-sm">{transaction.remarks || transaction.rejection_remarks}</span>
                    </div>
                  )}

                  {transaction.media_path && (
                    <div className="flex flex-col space-y-2 flex-grow">
                      <span className="text-xs text-gray-400 font-semibold block uppercase">Receipt</span>
                      {getFileType(transaction.media_path) === "image" ? (
                        <div className="border border-gray-200 rounded-xl bg-gray-50 overflow-hidden flex flex-col">
                          <div className="relative flex items-center justify-center p-2 bg-gray-100/50 min-h-[220px]">
                            <Zoom>
                              <img
                                src={transaction.media_path}
                                alt="Receipt"
                                className="max-h-80 w-full object-contain rounded-lg shadow-sm cursor-zoom-in"
                              />
                            </Zoom>
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 bg-white border-t border-gray-100">
                            <span className="text-xs font-semibold text-gray-500">
                              Receipt Image
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="border border-gray-200 rounded-xl bg-gray-50 overflow-hidden flex flex-col">
                          <div className="p-2 bg-gray-100/50 flex items-center justify-center min-h-[220px]">
                            <iframe
                              src={`${transaction.media_path}#toolbar=0&navpanes=0`}
                              title="Receipt PDF"
                              className="w-full h-[260px] rounded-lg border border-gray-200 bg-white"
                            />
                          </div>
                          <div className="flex items-center justify-between px-4 py-2.5 bg-white border-t border-gray-100">
                            <div className="flex items-center space-x-2">
                              <DocumentTextIcon className="w-4 h-4 text-red-500" />
                              <span className="text-xs font-semibold text-gray-500">
                                PDF Attachment
                              </span>
                            </div>
                            <a
                              href={transaction.media_path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              Open Fullscreen
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400 flex-1 flex items-center justify-center">
              No transaction data found.
            </div>
          )}
          <div className="flex justify-end pt-4 border-t border-gray-100 mt-auto flex-shrink-0">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PettyCashDetailModal;
